import { useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { CalendarCheck, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { useTranslation } from "react-i18next";

export default function BookConsultation() {
  const { t } = useTranslation();
  const { profile } = useOutletContext() || {};
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    practice_area: params.get("area") || "",
    preferred_date: "", preferred_time: "", message: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [booked, setBooked] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = t("book.errName");
    if (!form.email.trim()) errs.email = t("book.errEmail");
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = t("book.errEmailInvalid");
    if (!form.practice_area) errs.practice_area = t("book.errArea");
    if (!form.preferred_date) errs.preferred_date = t("book.errDate");
    if (!form.message.trim()) errs.message = t("book.errMessage");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await api.post("/public/inquiries", { ...form, type: "consultation" });
      setBooked(true);
      toast.success(t("book.toast"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "bg-transparent border-b-2 border-border focus:border-gold py-3 w-full outline-none transition-colors text-sm placeholder:text-muted-foreground/70";

  return (
    <div data-testid="book-consultation-page">
      <section className="bg-navy text-ivory hero-grain relative py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-4">{t("book.kicker")}</p>
          <h1 className="font-serif text-5xl sm:text-6xl font-semibold tracking-tight max-w-3xl">{t("book.title")}</h1>
          <p className="mt-5 text-ivory/70 max-w-2xl leading-relaxed">
            {t("book.intro")}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-5 gap-14">
        <div className="lg:col-span-2 space-y-6">
          {[
            ["01", t("book.step1Title"), t("book.step1Body")],
            ["02", t("book.step2Title"), t("book.step2Body")],
            ["03", t("book.step3Title"), t("book.step3Body")],
          ].map(([num, title, desc]) => (
            <div key={num} className="flex gap-6 bg-white border border-border p-6">
              <span className="font-serif text-3xl font-semibold text-gold">{num}</span>
              <div>
                <h3 className="font-serif text-xl font-semibold text-navy">{title}</h3>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
          <div className="bg-navy text-ivory p-6 flex gap-4 items-start">
            <Lock className="h-5 w-5 text-gold shrink-0 mt-1" />
            <p className="text-sm text-ivory/70 leading-relaxed">
              {t("book.confidential")}
            </p>
          </div>
        </div>

        <div className="lg:col-span-3">
          {booked ? (
            <div className="bg-white border border-border p-12 text-center" data-testid="booking-success">
              <CheckCircle2 className="h-12 w-12 text-gold-700 mx-auto mb-5" />
              <h2 className="font-serif text-3xl font-semibold text-navy">{t("book.received")}</h2>
              <p className="text-muted-foreground mt-3 max-w-md mx-auto">
                {t("book.thanks", { name: form.name.split(" ")[0], date: form.preferred_date })}
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-border p-8 sm:p-12" data-testid="booking-form" noValidate>
              <div className="flex items-center gap-3 mb-10">
                <CalendarCheck className="h-6 w-6 text-gold-700" />
                <h2 className="font-serif text-3xl font-semibold text-navy">{t("book.formTitle")}</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                <Field label={t("book.fullName")} error={errors.name} htmlFor="booking-name">
                  <input id="booking-name" data-testid="booking-name-input" value={form.name} onChange={set("name")} placeholder="Jane A. Smith" className={inputCls} />
                </Field>
                <Field label={t("book.emailAddress")} error={errors.email} htmlFor="booking-email">
                  <input id="booking-email" type="email" data-testid="booking-email-input" value={form.email} onChange={set("email")} placeholder="jane@example.com" className={inputCls} />
                </Field>
                <Field label={t("book.phone")} htmlFor="booking-phone">
                  <input id="booking-phone" data-testid="booking-phone-input" value={form.phone} onChange={set("phone")} placeholder="+1 (312) 555-0100" className={inputCls} />
                </Field>
                <Field label={t("book.area")} error={errors.practice_area} htmlFor="booking-area">
                  <select id="booking-area" data-testid="booking-area-select" value={form.practice_area} onChange={set("practice_area")} className={`${inputCls} bg-white`}>
                    <option value="">{t("book.selectArea")}</option>
                    {(profile?.practice_areas || []).map((a) => (
                      <option key={a.id} value={a.title_en || a.title}>{a.title}</option>
                    ))}
                    <option value="Other">{t("common.other")}</option>
                  </select>
                </Field>
                <Field label={t("book.date")} error={errors.preferred_date} htmlFor="booking-date">
                  <input id="booking-date" type="date" data-testid="booking-date-input" value={form.preferred_date}
                    min={new Date().toISOString().split("T")[0]} onChange={set("preferred_date")} className={inputCls} />
                </Field>
                <Field label={t("book.time")} htmlFor="booking-time">
                  <select id="booking-time" data-testid="booking-time-select" value={form.preferred_time} onChange={set("preferred_time")} className={`${inputCls} bg-white`}>
                    <option value="">{t("book.anyTime")}</option>
                    <option value="09:00">{t("book.morning")}</option>
                    <option value="13:00">{t("book.earlyAfternoon")}</option>
                    <option value="15:00">{t("book.lateAfternoon")}</option>
                  </select>
                </Field>
              </div>
              <div className="mt-8">
                <Field label={t("book.matter")} error={errors.message} htmlFor="booking-message">
                  <textarea id="booking-message" rows={4} data-testid="booking-message-input" value={form.message} onChange={set("message")}
                    placeholder={t("book.placeholderMatter")}
                    className={`${inputCls} resize-none`} />
                </Field>
              </div>
              <button type="submit" disabled={sending} data-testid="booking-submit-btn"
                className="mt-10 bg-gold text-navy hover:bg-gold-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 font-semibold uppercase tracking-wider text-sm px-10 py-4">
                {sending ? t("book.submitting") : t("book.submit")}
              </button>
              <p className="mt-4 text-xs text-muted-foreground">
                {t("book.noRelationship")}
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
